'use client';

import Image from 'next/image';
import Link from 'next/link';
import { BsCalendar2EventFill } from 'react-icons/bs';
import Button from '@src/components/atoms/button';
import { useState, useMemo } from 'react';
import {
  useQueryArticleList,
  useQueryArticleCategory,
} from '@src/apis/article';

const FilterItem = ({ filter, currentValue, onClick }) => {
  const isActive = filter.id === currentValue.id;

  return (
    <button
      type="button"
      className={`flex items-center justify-center h-[32px] px-[12px] text-[#686A68] bg-[#FAFAFA] font-satoshi rounded-[24px] text-[14px] ${
        isActive
          ? 'border border-solid border-[#252526] bg-[#DEDFDE] text-[#252526] font-satoshi-bold'
          : ''
      }`}
      onClick={() => onClick(filter)}
    >
      {filter.name}
    </button>
  );
};

// Helper to create article slug
const createArticleSlug = (article) => {
  if (!article) return '';
  const slugifiedTitle = article.title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .substring(0, 60);
  return `${article.id}-${slugifiedTitle}`;
};

const ListArticle = () => {
  const [currentFilter, setCurrentFilter] = useState({
    id: null,
    name: 'Semua',
  });
  const limit = 10;

  const { data: categories } = useQueryArticleCategory();
  const {
    data: articles,
    isLoading: articlesLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useQueryArticleList({
    _limit: limit,
    category_id: currentFilter.id || undefined,
  });

  const articleData = useMemo(() => {
    return articles?.pages?.flatMap((page) => page?.data?.data || []) || [];
  }, [articles]);

  const filters = [
    { id: null, name: 'Semua' },
    ...(categories?.data?.data || []),
  ];

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const handleFilterChange = (filter) => {
    setCurrentFilter(filter);
  };

  return (
    <div
      className="py-[32px] px-[48px] bg-[#FAFAFA] 
    dd2-2xl:p-6"
    >
      <div className="flex items-center gap-2 mb-6">
        {filters.map((filter) => (
          <FilterItem
            key={filter.id || 'all'}
            filter={filter}
            currentValue={currentFilter}
            onClick={handleFilterChange}
          />
        ))}
      </div>

      {articlesLoading ? (
        <div className="grid grid-cols-4 gap-x-6 gap-y-[40px] dd2-2xl:grid-cols-3 dd2-2xl:gap-x-4 dd2-2xl:gap-y-[32px] dd2-xl:grid-cols-2 dd2-lg:grid-cols-1">
          {[...Array(8)].map((_, idx) => (
            <div
              key={idx}
              className="rounded-[2px] bg-white border border-[0.5px] border-[#DEDFDE] animate-pulse"
            >
              <div className="w-full h-[179px] bg-gray-200 rounded-t-[2px]" />
              <div className="p-4">
                <div className="h-4 bg-gray-200 rounded mb-2" />
                <div className="h-4 bg-gray-200 rounded w-3/4" />
              </div>
            </div>
          ))}
        </div>
      ) : !!articleData?.length ? (
        <div
          className="grid grid-cols-4 gap-x-6 gap-y-[40px] 
        dd2-2xl:grid-cols-3 dd2-2xl:gap-x-4 dd2-2xl:gap-y-[32px]
        dd2-xl:grid-cols-2
        dd2-lg:grid-cols-1"
        >
          {articleData.map((article) => {
            const isGiveback = article?.category?.name?.replace(/\s+/g, '').toLowerCase() === 'giveback';
            const articleSlug = createArticleSlug(article);

            return (
              <Link
                key={article.id}
                href={`/stories/${articleSlug}`}
                className="rounded-[2px] bg-white border border-[0.5px] border-[#DEDFDE] cursor-pointer hover:shadow-md transition-shadow block"
              >
                <Image
                  src={article.main_image}
                  alt={article.title}
                  width={1000}
                  height={179}
                  className="w-full h-[179px] object-cover rounded-t-[2px] 
                 dd2-2xl:h-[171px] 
                 dd2-xl:h-[144px]
                 dd2-lg:h-[184px]"
                />
                <h2 className="text-[17px] font-satoshi-bold text-[#252526] py-2 px-4 line-clamp-2">
                  {article.title}
                </h2>

                <div className="flex items-center justify-between py-2 px-4 border-t-[0.5px] border-dashed border-[#DEDFDE]">
                  <p className="text-[12px] font-inter text-[#808285] flex items-center gap-1">
                    <BsCalendar2EventFill />
                    {article.created_at}
                  </p>

                  <span className={`mb-2 w-fit bg-[#F5F5F5] text-[#686A68] rounded-[2px] px-2 h-[18px] flex items-center justify-center text-[11px] font-satoshi-bold ${isGiveback ? '!bg-[#F3EFE2] !text-[#A19276]' : ''}`}>
                    {article?.category?.name?.toUpperCase()}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-[#686A68] font-satoshi">
            Tidak ada artikel ditemukan
          </p>
        </div>
      )}
      {!!articleData?.length && hasNextPage && (
        <Button
          onClick={handleLoadMore}
          disabled={isFetchingNextPage}
          className="mt-6 h-[32px] !px-[12px] !text-[#252526] !bg-white border border-solid border-[#252526] !py-0 
        dd2-lg:w-full"
        >
          {isFetchingNextPage ? 'Memuat...' : 'Tampilkan Lebih Banyak'}
        </Button>
      )}
    </div>
  );
};

export default ListArticle;
